import { scaleOrdinal, scaleLinear, scaleDiverging, scaleSequential } from "d3-scale";
import { formatFloat, formatInt } from "./map/language";
import { toObject, darken } from "./utils";
import { generateContinuousMapboxExpression } from "./map/mapbox_helpers";

// ///////////////////////////////////////////////////////////
// DATA
// ///////////////////////////////////////////////////////////

const generateSteps = (start, end, steps) => {
	if (steps < 2) {
		throw new Error("Steps must be at least 2 to include both start and end values.");
	}

	const stepSize = (end - start) / (steps - 1);
	const result = Array.from({ length: steps }, (_, i) => start + i * stepSize);

	return result;
};

export const colorScaleTransparent = scaleLinear()
	.domain(generateSteps(0, 100, 2))
	.range(["transparent", "transparent"])
	.clamp(true);

const colorScaleYear = scaleLinear()
	.domain(generateSteps(0, 15, 7))
	.range(["#F8F4E7", "#FBF6CC", "#FFE37E", "#FF9A7A", "#FE6F6F", "#D9488E", "#922382"])
	.clamp(true);

// const colorScaleAbsolute = scaleLinear()
// 	.domain(generateSteps(0, 100, 5))
// 	.range(["#FFE37E", "#FF9A7A", "#FE6F6F", "#D9488E", "#922382"])
// 	.clamp(true);
// TF: workaround because they should all have the same color --> this was easier 🙈
const colorScaleAbsolute = scaleLinear().domain(generateSteps(0, 100, 2)).range(["#F16074", "#F16074"]).clamp(true);

const colorScaleChange = scaleLinear()
	.domain([-1.0, -0.5, -0.1, 0, 0.1, 0.5, 1.0])
	.range(["#38ADBD", "#A7E8E4", "#DFEFEB", "#F2F2F2", "#FFDDC5", "#FFA296", "#FF6363"])
	.clamp(true);

export const toggleButtons = [
	{
		id: "year_pp",
		visType: "choropleth",
		label: "pro 1000 Personen",
		legendText: "Fahrraddiebstähle pro 1000 Einw. von August 2022 bis Juli 2023",
		legendTickValues: [0, 10, 20],
		legendTickLabels: ["0", "10", ">20"],
		barMaxValue: 25,
		tooltipText: "Fahrraddiebstähle pro 1000 Einw.<br>(Aug 22 bis Jul 23)",
		colorScale: colorScaleYear,
		color: "#922382",
		width: 160,
		formatValue: formatInt(),
	},
	{
		id: "year_pp_total",
		visType: "points",
		label: "gesamt",
		legendText: "Fahrraddiebstähle gesamt von August 2022 bis Juli 2023",
		// legendTickValues: [0, 50, 100],
		// legendTickLabels: ["0", "50", "100"],
		barMaxValue: 150,
		tooltipText: "Fahrraddiebstähle gesamt<br>(Aug 22 bis Jul 23)",
		colorScale: colorScaleAbsolute,
		color: "#F16074",
		width: 100,
		formatValue: formatInt(),
	},
	{
		id: "change",
		visType: "choropleth",
		label: "Veränderung",
		legendText: "Veränderung zum Vorjahreszeitraum",
		legendTickValues: [-1.0, 0, 1.0],
		legendTickLabels: ["weniger", "⌀", "mehr"],
		tooltipText: "Veränderung",
		tooltipSubtitle: {
			muchmuchless: "Die Anzahl der Diebstähle nimmt sehr stark ab, während sie im Berliner Schnitt zunimmt (+3,8%).", // < -20%
			muchless: "Die Anzahl der Diebstähle nimmt stark ab, während sie im Berliner Schnitt zunimmt (+3,8%).", // > -20% < -10%
			less: "Die Anzahl der Diebstähle nimmt ab, während sie im Berliner Schnitt zunimmt (+3,8%).", // > -10% < 0%
			lesssimilar: "Die Anzahl der Diebstähle nimmt weniger stark zu als im Berliner Schnitt (+3,8%).", // > 0% < 2%
			similar: "Die Anzahl der Diebstähle nimmt ähnlich stark zu als im Berliner Schnitt (+3,8%).", // > 2% < 5%
			more: "Die Anzahl der Diebstähle nimmt stärker zu als im Berliner Schnitt (+3,8%).", // > 5% < 10%
			muchmore: "Die Anzahl der Diebstähle nimmt viel stärker zu als im Berliner Schnitt (+3,8%).", // > 10% < 20%
			muchmuchmore: "Die Anzahl der Diebstähle nimmt sehr viel stärker zu als im Berliner Schnitt (+3,8%).", // > 20%
		},
		colorScale: colorScaleChange,
		color: "#38ADBD",
		width: 120,
		formatValue: formatFloat({ multiplier: 100, signed: true, postfix: "%", adaptive: true, decimal: true }),
	},
];

export const toggleButtonsById = toObject(toggleButtons, "id");

toggleButtons.forEach((button) => {
	if (button.visType !== "points") return;

	const colors = button.colorScale.range();
	const domain = button.colorScale.domain();

	// get a mapbox expression for a continous color scale
	const colorExpression = generateContinuousMapboxExpression(colors, domain, button.id);

	const darkenedColors = colors.map((color) => darken(color, 0.3)); // Darken each color by 20%
	const darkenedExpression = generateContinuousMapboxExpression(darkenedColors, domain, button.id).slice(3);

	// Create the new structure with feature-state check
	button.mapboxColorExpression = [
		"case",
		["==", ["get", button.id], null],
		"#D9D9D9",
		[
			"case",
			["any", ["boolean", ["feature-state", "current"], false], ["boolean", ["feature-state", "hovered"], false]], // Check if 'current' or 'hovered' feature-state is true
			...darkenedExpression, // Apply darken if feature-state is true
			...colorExpression.slice(3), // Fallback to the original interpolate expression if feature-state is false
		],
	];
});

export const datesStrings = ["July 2022", "October 2022", "January 2023", "April 2023", "July 2023"];

export const dates = datesStrings.map((dateStr) => {
	const [monthName, year] = dateStr.split(" ");
	const monthNumber = new Date(Date.parse(`${monthName} 1, 2012`)).getMonth();
	return new Date(year, monthNumber);
});

export const dateLookup = datesStrings.reduce((obj, dateStr, i) => {
	obj[dateStr] = dates[i];
	return obj;
}, {});

// ///////////////////////////////////////////////////////////
// MAP
// ///////////////////////////////////////////////////////////

export const initialBounds = {
	desktop: [
		[12.829037255184318, 52.331871326934845],
		[14.102583501525999, 52.68177005131605],
	],
	tablet: [
		[13.211867193817284, 52.27179558551268],
		[13.899423327757603, 52.735101645391296],
	],
	mobile: [
		[13.050862498603664, 52.27129844848395],
		[13.79080489372484, 52.68263346358415],
	],
};

export const maxBounds = [
	[12.480232311625713, 51.94344715267028], // south west
	[14.381828464193688, 52.95668580240292], // north east
];

export const initialSelectedLocation = { lng: 13.389473278400644, lat: 52.51709032125603 };

export const baseChoroplethOpacity = 0.75;
export const baseChoroplethOpacityLuftbilder = 0.5;
export const basePointsOpacity = 1;
export const basePointsStrokeOpacity = 0.9;

export const bezirke = {
	"01": "Mitte",
	"02": "Friedrichshain-Kreuzberg",
	"03": "Pankow",
	"04": "Charlottenburg-Wilmersdorf",
	"05": "Spandau",
	"06": "Steglitz-Zehlendorf",
	"07": "Tempelhof-Schöneberg",
	"08": "Neukölln",
	"09": "Treptow-Köpenick",
	10: "Marzahn-Hellersdorf",
	11: "Lichtenberg",
	12: "Reinickendorf",
};

export const markerData = [{ n: "fernsehturm", lat: 52.52084869043665, lon: 13.409393064444167 }];
